// src/utils/layout/repositionLayout.js
//
// Reposition-only mode: icerik degismeden sadece pozisyonlari yeniden hesapla.

import * as THREE from 'three';
import { dxfDrawCore } from '../../../modules/dxf';
import { createMainFrameParts } from '../../../utils/simpleTransformFunctions';
import { LAYOUT_GROUP_ORDER, DOC_NAME_TO_GROUP, FALLBACK_LAYOUT_GROUP } from '../../../constants/projectTypes';
import { hideOutlierMeshes } from './layoutHelpers';
import { setGlobalDocumentBounds } from '../../../utils/getActiveFloorPlanId';
import { tryPlaceInCol } from './columnPacking';

export function repositionLayout(mainFrame, storedDocs, layoutSettings, maxColumnHeight, DOC_OFFSET, MAIN_FRAME_HEIGHT) {
  const DIRECTION = layoutSettings.direction || 'ltr';
  const VERTICAL_ALIGN = layoutSettings.verticalAlign || 'bottom';

  // Eski frame content'i kaldir
  const frameChildren = mainFrame.children.filter(c => c.userData?.isFrameContent);
  frameChildren.forEach(c => {
    c.traverse(ch => {
      if (ch.geometry) ch.geometry.dispose();
      if (ch.material) {
        const mats = Array.isArray(ch.material) ? ch.material : [ch.material];
        mats.forEach(mat => { if (mat.map) mat.map.dispose(); mat.dispose(); });
      }
    });
    mainFrame.remove(c);
  });

  // Yeniden paketleme
  const groupCols = {};
  LAYOUT_GROUP_ORDER.forEach(g => { groupCols[g] = []; });
  const pinnedCols = [];

  storedDocs.forEach(doc => {
    const docH = (doc.docDef.name === 'Kat Plan\u0131' || doc.docDef.name === 'Ground Floor')
      ? maxColumnHeight : doc.h;
    if (doc.isPinned) {
      const col = { x: 0, height: 0, maxWidth: 0, docs: [] };
      pinnedCols.push(col);
      col.docs.push({ group: doc.group, docDef: doc.docDef, w: doc.w, h: docH, subX: 0, subY: 0 });
      col.height = docH; col.maxWidth = doc.w;
    } else {
      const targetCols = groupCols[doc.groupKey];
      let chosenCol = targetCols[targetCols.length - 1];
      let chosenPos = chosenCol ? tryPlaceInCol(chosenCol, doc.w, docH, maxColumnHeight, DOC_OFFSET) : null;
      if (!chosenPos) {
        chosenCol = { x: 0, height: 0, maxWidth: 0, docs: [] };
        targetCols.push(chosenCol);
        chosenPos = tryPlaceInCol(chosenCol, doc.w, docH, maxColumnHeight, DOC_OFFSET) || { subX: 0, subY: 0 };
      }
      chosenCol.docs.push({ group: doc.group, docDef: doc.docDef, w: doc.w, h: docH, subX: chosenPos.subX, subY: chosenPos.subY });
      chosenCol.height = Math.max(chosenCol.height, chosenPos.subY + docH);
      chosenCol.maxWidth = Math.max(chosenCol.maxWidth, chosenPos.subX + doc.w);
    }
  });

  // Pozisyon ve hizalama
  const GROUP_GAP = DOC_OFFSET * 2;
  let cursorX = 0;
  const allCols = [];
  const computeShift = (colHeight) => {
    const free = maxColumnHeight - colHeight;
    if (VERTICAL_ALIGN === 'top') return 0;
    if (VERTICAL_ALIGN === 'center') return free / 2;
    return free;
  };
  const positionCol = (col) => {
    col.x = cursorX;
    cursorX += col.maxWidth + DOC_OFFSET;
    col.docs.forEach(item => { item.group.position.set(col.x + item.subX, -DOC_OFFSET - item.subY, 0); });
    const shift = computeShift(col.height);
    if (Math.abs(shift) > 0.01) col.docs.forEach(item => { item.group.position.y -= shift; });
    allCols.push(col);
  };
  LAYOUT_GROUP_ORDER.forEach(gk => {
    const arr = groupCols[gk];
    if (arr.length === 0) return;
    arr.forEach(positionCol);
    cursorX += GROUP_GAP - DOC_OFFSET;
  });
  pinnedCols.forEach(positionCol);

  if (DIRECTION === 'rtl') {
    allCols.forEach(col => {
      col.docs.forEach(item => { item.group.position.x = cursorX - (item.group.position.x + item.w); });
    });
  }

  // Yeni frame polyline
  const frameWidth = cursorX + DOC_OFFSET;
  const mainFrameParts = createMainFrameParts(frameWidth, MAIN_FRAME_HEIGHT, DOC_OFFSET);
  const frameGroup = new THREE.Group();
  dxfDrawCore(frameGroup, mainFrameParts, [], "scene");
  frameGroup.userData.isFrameContent = true;
  if (frameGroup.children.length > 0) mainFrame.add(frameGroup);
  hideOutlierMeshes(mainFrame, 0, 'reposition mainFrameGroup');

  // Document bounds guncelle
  const newDocBounds = [];
  allCols.forEach(col => {
    col.docs.forEach(item => {
      if (item.docDef.name === 'Kat Plan\u0131' && item.docDef.floorPlanId) {
        const pos = item.group.position;
        newDocBounds.push({
          floorPlanId: item.docDef.floorPlanId,
          name: item.docDef.displayName || item.docDef.name,
          minX: pos.x, maxX: pos.x + item.w, minY: pos.y - item.h, maxY: pos.y,
        });
      }
    });
  });
  setGlobalDocumentBounds(newDocBounds);

  mainFrame.userData.frameBounds = { frameWidth, frameHeight: MAIN_FRAME_HEIGHT, docOffset: DOC_OFFSET };
  return {
    frameWidth, documentBounds: newDocBounds,
    bounds: { frameWidth, frameHeight: MAIN_FRAME_HEIGHT, minX: 0, maxX: frameWidth, minY: -MAIN_FRAME_HEIGHT, maxY: 0 },
  };
}
